import { useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import drilldown from "highcharts/modules/drilldown";
import accessibility from "highcharts/modules/accessibility";
import dayjs from "../../../../utils/dayjs-util";
import { getFrequencyInterval } from "../../../../utils/frequency-util";
import { getTimeFormat } from "../../../../utils/date-util";
import { getAESTEndTime, getAESTStartTime } from "../../../../utils/date-util";
import { fetchSubscriptionThroughputHistoryData } from "../../../../apis/subscriptions-apis";
import {
    formatSubscriptionUplinkThroughPutData,
    roundOffThroughputValue,
} from "../../../../utils/throughput-data-util";

drilldown(Highcharts);
accessibility(Highcharts);

const UpLinkHighChartsWrapper = ({
    subscriptionId,
    formattedSubscriptionThroughputData = [],
    period,
    setPeriod,
    dateTimeFrom,
    dateTimeTo,
    selectedOption,
    setSubscriptionUplinkThroughputData,
    subscriptionData,
    chartRef,
}) => {
    let periodVar; //Declared local variable to update period when it is drill down
    // Checking formattedSubscriptionThroughputData and formattedNetworkThroughputData, if data doesn't exist do not load chart
    // Also checking to render only if period is daily and selected option is last 30 days (or)
    // selected option = today, because this should not render if selected option is last 30 days and period = hourly
    const options =
        (period === "daily" && selectedOption === "displaylastdays") || selectedOption === "today"
            ? {
                  chart: {
                      type: "column",
                      events: {
                          drilldown: async function (e) {
                              if (!e.seriesOptions) {
                                  let chart = this;
                                  const aestTimeFromHourly = getAESTStartTime(e.point.x);
                                  const aestTimeToHourly = getAESTEndTime(e.point.x);
                                  const dateTimeFromHourly = aestTimeFromHourly.utc().format("YYYY-MM-DDTHH:mm:ss");
                                  const dateTimeToHourly = aestTimeToHourly.utc().format("YYYY-MM-DDTHH:mm:ss");
                                  periodVar = "hourly";
                                  setPeriod("hourly");

                                  const subThroughput = await fetchSubscriptionThroughputHistoryData(subscriptionId, {
                                      params: {
                                          period: "hourly",
                                          dateTimeFrom: dateTimeFromHourly,
                                          dateTimeTo: dateTimeToHourly,
                                      },
                                  });

                                  if (subThroughput) {
                                    setSubscriptionUplinkThroughputData(subThroughput);
                                      const subsThroughputData = formatSubscriptionUplinkThroughPutData(
                                          subThroughput,
                                          "hourly"
                                      );
                                      const subsDataLength = subsThroughputData.values.length;

                                      chart.addSingleSeriesAsDrilldown(e.point, {
                                          name: "Uplink throughput",
                                          data: subsThroughputData.values,
                                          color: "#0064d2",
                                          stack: "UpLink",
                                          groupPadding: subsDataLength >= 10 ? 0.1 : 0,
                                          pointPadding: subsDataLength >= 10 ? 0.1 : 0,
                                          pointPlacement: 0,
                                          maxPointWidth: 20,
                                          borderWidth: 0,
                                          shadow: false,
                                      });
                                      chart.applyDrilldown();
                                      const chartFreqInterval = getFrequencyInterval("Hourly") * 3;
                                      const chartMin =
                                          aestTimeFromHourly.utc().valueOf() / 1000 - getFrequencyInterval("Hourly");
                                      const chartMax = aestTimeToHourly.utc().valueOf() / 1000;
                                      chart.xAxis[0].update({
                                          tickInterval: chartFreqInterval,
                                          min: chartMin,
                                          max: chartMax,
                                      });
                                  }
                              }
                          },
                          render: function () {
                              const chart = this;

                              if (
                                  (period === "daily" &&
                                      periodVar !== "hourly" &&
                                      selectedOption === "displaylastdays") ||
                                  selectedOption === "today"
                              ) {
                                  if (chart.breadcrumbs) {
                                      chart.breadcrumbs.destroy();
                                  }
                              }
                          },
                      },
                  },
                  title: {
                      text: "",
                  },
                  subtitle: {
                      text: "",
                  },
                  legend: {
                      itemDistance: 60,
                      align: "center",
                      verticalAlign: "bottom",
                      x: 0,
                      y: 0,
                      labelFormatter: function () {
                          return (
                              '<div class="legend-item"><span>' +
                              (this.name.toLowerCase() === "uplink throughput"
                                  ? "Avg speed with LANES"
                                  : "") +
                              "</span></div>"
                          );
                      },
                  },
                  tooltip: {
                      formatter: function () {
                          var tooltip =
                              '<table><tr><th colspan="2" style="color: rgb(65, 65, 65);"><b>Average speed</b></th></tr>';
                          this.points.forEach((point, index, array) => {
                              tooltip +=
                                  '<tr><td width="40%"><hr style="padding:0; margin:0; white-space:nowrap; background-color: ' +
                                  point.series.color +
                                  ";color: " +
                                  point.series.color +
                                  '; height: 3px;width:100%; float: left;border: none;" /></td> <td style="text-align: right; white-space:nowrap;">' +
                                  roundOffThroughputValue(
                                      point.series.name.toLowerCase() === "uplink throughput" ? point.total : point.y
                                  ) +
                                  'Mbps</td><td style="text-align: left; padding:0; margin:0; white-space:nowrap;">' +
                                  (point.series.name.toLowerCase() === "uplink throughput"
                                      ? "with LANES"
                                      : "") +
                                  "</td></tr>";
                          });
                          tooltip +=
                              '<tr><td colspan="2">' +
                              ((periodVar && periodVar.toLowerCase() === "hourly") || period === "hourly"
                                  ? getTimeFormat(this.x, periodVar !== undefined ? periodVar : period, "datetime")
                                  : getTimeFormat(
                                        this.x,
                                        periodVar !== undefined ? periodVar : period,
                                        "daily",
                                        true
                                    )) +
                              "</td></tr></table>";
                          return tooltip;
                      },
                      shared: true,
                      crosshairs: false,
                      borderColor: "rgb(250, 250, 250)",
                      shdow: true,
                      backgroundColor: "rgb(255, 255, 255)",
                      useHTML: true,
                      borderWidth: 1,
                      style: {
                          color: "rgb(119, 119, 119)",
                      },
                  },
                  credits: {
                      enabled: false,
                  },
                  yAxis: {
                      title: {
                          text: "",
                      },
                      labels: {
                          formatter: function () {
                              return `${this.value}Mbps`;
                          },
                      },
                      lineWidth: 2,
                      lineColor: "#8F8F8F",
                      gridLineWidth: 1,
                      gridLineColor: "#8F8F8F",
                      gridLineDashStyle: "Dot",
                  },
                  xAxis: {
                      min:
                          periodVar && periodVar.toLowerCase() === "daily"
                              ? dayjs.utc(dateTimeFrom).valueOf() / 1000 - getFrequencyInterval(period)
                              : selectedOption === "today"
                              ? dayjs().tz("Australia/Melbourne").startOf("day").utc().valueOf() / 1000 -
                                getFrequencyInterval("Hourly")
                              : dayjs.utc(dateTimeFrom).startOf("day").valueOf() / 1000 -
                                getFrequencyInterval(periodVar !== undefined ? periodVar : period),
                      max:
                          periodVar && periodVar.toLowerCase() === "daily"
                              ? dayjs.utc(dateTimeTo).valueOf() / 1000 + getFrequencyInterval(period)
                              : selectedOption === "today"
                              ? dayjs().tz("Australia/Melbourne").endOf("day").utc().valueOf() / 1000
                              : dayjs.utc(dateTimeTo).endOf("day").valueOf() / 1000 +
                                getFrequencyInterval(periodVar !== undefined ? periodVar : period),
                      tickInterval: getFrequencyInterval(period) * 3,
                      style: {
                          fontSize: "14px",
                      },
                      labels: {
                          rotation: 0,
                          formatter: function (tick) {
                              const formattedTick = getTimeFormat(
                                  tick.value,
                                  periodVar !== undefined ? periodVar : period
                              );
                              return formattedTick;
                          },
                          style: {
                              fontSize: "12px",
                          },
                      },
                      lineColor: "#8F8F8F",
                      lineWidth: 2,
                  },
                  plotOptions: {
                      column: {
                          stacking: "normal",
                          minPointLength: 3,
                      },
                      series: {
                          pointPadding: 0.1,
                          groupPadding: 0,
                          borderWidth: 0,
                          shadow: false,
                      },
                  },
                  series: [
                      {
                          name: "Uplink throughput",
                          data: formattedSubscriptionThroughputData.values,
                          color: "#0064d2",
                          stack: "Uplink",
                          maxPointWidth: 20,
                      },
                  ],
                  drilldown: {
                      activeAxisLabelStyle: {
                          cursor: "default",
                          fontWeight: "normal",
                          textDecoration: "none",
                      },
                      breadcrumbs: {
                          floating: false,
                          formatter: function (e) {
                              if (
                                  ((periodVar && periodVar.toLowerCase() === "hourly") || period === "hourly") &&
                                  selectedOption !== "today"
                              ) {
                                  if (e.level === 0) {
                                      return "Daily Performance";
                                  } else {
                                      return `Hourly ${e.levelOptions.name}`;
                                  }
                              } else {
                                  return;
                              }
                          },
                          events: {
                              click: function (button, breadcrumbs, e) {
                                  setPeriod("daily");
                                  setSubscriptionUplinkThroughputData(subscriptionData);
                              },
                          },
                      },
                      series: [],
                  },
              }
            : {};

    /** Due to accessibility requirements they have requested to reduce the verbosity
     * Accessing the DOM and modifying it like this is bad practice, but I have no alternative as
     * I do not have access to the virtual dom and can't find an API reference.
     */
    useEffect(() => {
        const highchartsRoot = document.getElementsByClassName("highcharts-root");
        if (highchartsRoot.length) {
            for (let element of highchartsRoot) {
                Array.from(element.querySelectorAll("desc"))
                    .find((el) => el.textContent === "Created with Highcharts 10.3.3")
                    ?.remove();
            }
        }
    }, []);

    return <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />;
};

export default UpLinkHighChartsWrapper;
